"use client"

import React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { CheckCircle, Loader2 } from "lucide-react"
import { useSettings } from "@/lib/settings-context"

const projectTypes = [
  { value: "hdd", label: "Horizontal Directional Drilling (HDD)" },
  { value: "micro-trenching", label: "Micro Trenching" },
  { value: "fiber-pulling", label: "Fiber Pulling" },
  { value: "fiber-splicing", label: "Fiber Splicing" },
  { value: "aerial", label: "Aerial Installation" },
  { value: "underground", label: "Underground Installation" },
  { value: "other", label: "Other / Multiple Services" },
]

export function ContactForm() {
  const { settings } = useSettings()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    setIsSubmitting(false)
    setIsSubmitted(true)
  }

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-border/50 bg-card p-12 text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle className="h-8 w-8 text-primary" />
        </div>
        <h3 className="mb-2 text-2xl font-bold text-foreground">Thank You!</h3>
        <p className="text-muted-foreground">
          Your message has been received. Our team will contact you within 24 hours to discuss your project.
        </p>
        <Button 
          className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => setIsSubmitted(false)}
        >
          Submit Another Request
        </Button>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border/50 bg-card p-6 md:p-8">
      <h2 className="mb-6 text-2xl font-bold text-foreground">Request a Quote</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Fields */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input 
              id="firstName" 
              name="firstName" 
              placeholder="John" 
              required 
              className="bg-background"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input 
              id="lastName" 
              name="lastName" 
              placeholder="Doe" 
              required 
              className="bg-background"
            />
          </div>
        </div>

        {/* Contact Fields */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email" 
              name="email" 
              type="email" 
              placeholder="john@company.com" 
              required 
              className="bg-background"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input 
              id="phone" 
              name="phone" 
              type="tel" 
              placeholder={settings.phoneNumber} 
              className="bg-background"
            />
          </div>
        </div>

        {/* Company */}
        <div className="space-y-2">
          <Label htmlFor="company">Company Name</Label>
          <Input 
            id="company" 
            name="company" 
            placeholder="Your Company Inc." 
            className="bg-background"
          />
        </div>

        {/* Project Type */}
        <div className="space-y-2">
          <Label htmlFor="projectType">Project Type</Label>
          <Select name="projectType" required>
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {projectTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Project Details */}
        <div className="space-y-2">
          <Label htmlFor="message">Project Details</Label>
          <Textarea 
            id="message" 
            name="message" 
            placeholder="Tell us about your project, location, timeline, and any specific requirements..."
            rows={5}
            required
            className="bg-background resize-none"
          />
        </div>

        {/* Submit Button */}
        <Button 
          type="submit" 
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            "Submit Request"
          )}
        </Button>
      </form>
    </div>
  )
}
